import axios, {
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig
} from "axios";

const env = process.env.VITE_YA_DOMAIN ? process.env : import.meta.env;

export const normalizeEnvValue = (value?: string): string =>
  (value || "")
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .trim();

export const normalizeDomain = (domain?: string): string =>
  normalizeEnvValue(domain)
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

export const DOMAIN: string = normalizeDomain(env.VITE_YA_DOMAIN);
export const VITE_API_DOMAIN: string = normalizeEnvValue(env.VITE_API_DOMAIN);
export const VITE_API_BASEURL: string = normalizeEnvValue(env.VITE_API_BASEURL);
export const VITE_RESERVE_BASEURL: string = normalizeEnvValue(
  env.VITE_RESERVE_BASEURL
);

export const timeZone = -(new Date().getTimezoneOffset() / 60);

export const normalizeApiBaseUrl = (url: string): string => {
  const normalizedUrl = normalizeEnvValue(url).replace(/\/+$/, "");

  if (!normalizedUrl) {
    return "/api";
  }

  if (normalizedUrl.startsWith("/")) {
    return normalizedUrl.endsWith("/api")
      ? normalizedUrl
      : `${normalizedUrl}/api`;
  }

  const baseUrl = normalizedUrl.startsWith("http")
    ? normalizedUrl
    : `https://${normalizedUrl}`;
  return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
};

export const BASEURL = normalizeApiBaseUrl(
  VITE_API_BASEURL || VITE_API_DOMAIN || DOMAIN
);

export const postKeepalive = async (
  path: string,
  data: unknown
): Promise<void> => {
  const normalizedPath = path.replace(/^\/+/, "");
  const response = await fetch(`${BASEURL}/${normalizedPath}`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(data),
    keepalive: true
  });

  if (!response.ok) {
    throw new Error(`Keepalive request failed: ${response.status}`);
  }
};

const BACKUP_BASEURLS = [VITE_RESERVE_BASEURL]
  .filter(Boolean)
  .map(normalizeApiBaseUrl);

const REQUEST_TIMEOUT = 3000;
const GET_HEDGE_DELAY = 400;

const isReserveStatus = (status?: number): boolean =>
  status == 502 || status == 504 || status === undefined;

const getErrorStatus = (error: unknown): number | undefined =>
  axios.isAxiosError<unknown, unknown>(error)
    ? error.response?.status
    : undefined;

const requestFromBackup = async (
  originalRequest: InternalAxiosRequestConfig<unknown>
): Promise<AxiosResponse<unknown, unknown>> => {
  let lastError: unknown;

  for (const baseURL of BACKUP_BASEURLS) {
    try {
      const backupHTTP = axios.create({
        withCredentials: true,
        baseURL,
        timeout: REQUEST_TIMEOUT,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("keyUserAuth"),
          Accept: "application/json",
          timeZone
        }
      });

      return await backupHTTP<
        unknown,
        AxiosResponse<unknown, unknown>,
        unknown
      >({
        ...originalRequest,
        baseURL: undefined
      });
    } catch (backupError: unknown) {
      if (!isReserveStatus(getErrorStatus(backupError))) {
        throw backupError;
      }

      lastError = backupError;
    }
  }

  return Promise.reject(lastError);
};

const defaultAdapter = axios.getAdapter(axios.defaults.adapter);

const hedgedGetAdapter: AxiosAdapter = (config) => {
  if (config.method?.toLowerCase() !== "get" || BACKUP_BASEURLS.length === 0) {
    return defaultAdapter(config);
  }

  const baseURLs = [config.baseURL || BASEURL, ...BACKUP_BASEURLS];
  const controllers = baseURLs.map(() => new AbortController());
  const timers: ReturnType<typeof setTimeout>[] = [];

  return new Promise((resolve, reject) => {
    let failedRequests = 0;
    let settled = false;
    let lastError: unknown;

    const abortRequests = (): void => {
      controllers.forEach((controller) => controller.abort());
      timers.forEach(clearTimeout);
    };

    const onCallerAbort = (): void => {
      if (!settled) {
        settled = true;
        abortRequests();
        reject(new axios.CanceledError("Request canceled"));
      }
    };

    config.signal?.addEventListener?.("abort", onCallerAbort, {once: true});

    if (config.signal?.aborted) {
      onCallerAbort();
      return;
    }

    const finish = (response: AxiosResponse): void => {
      if (settled) {
        return;
      }

      settled = true;
      abortRequests();
      config.signal?.removeEventListener?.("abort", onCallerAbort);
      resolve(response);
    };

    const fail = (error: unknown): void => {
      if (settled) {
        return;
      }

      lastError = error;
      failedRequests += 1;
      if (failedRequests === baseURLs.length) {
        settled = true;
        config.signal?.removeEventListener?.("abort", onCallerAbort);
        reject(lastError);
      }
    };

    baseURLs.forEach((baseURL, index) => {
      const startRequest = (): void => {
        if (settled) {
          return;
        }

        defaultAdapter({
          ...config,
          baseURL,
          signal: controllers[index]?.signal
        }).then(finish, fail);
      };

      if (index === 0) {
        startRequest();
      } else {
        timers.push(setTimeout(startRequest, index * GET_HEDGE_DELAY));
      }
    });
  });
};

export const HTTP = axios.create({
  withCredentials: true,
  baseURL: BASEURL,
  timeout: REQUEST_TIMEOUT,
  adapter: hedgedGetAdapter,
  headers: {
    Authorization: "Bearer " + localStorage.getItem("keyUserAuth"),
    Accept: "application/json",
    timeZone
  }
});

HTTP.interceptors.response.use(
  function (
    response: AxiosResponse<unknown, unknown>
  ): AxiosResponse<unknown, unknown> {
    return response;
  },
  async function (
    error: unknown
  ): Promise<AxiosResponse<unknown, unknown> | void> {
    if (!axios.isAxiosError<unknown, unknown>(error) || axios.isCancel(error)) {
      throw error;
    }

    const status = error.response?.status;
    const originalRequest = error.config;

    let host = window.location.hostname;
    if (status == 401 && (host == DOMAIN || host == "localhost")) {
      const path = document.location.pathname;
      const isNotAuth = ["/policy", "/widget", "/"];
      const quiz =
        !path.includes("/quiz/setting") &&
        path != "/quiz" &&
        path.includes("/quiz");
      if (isNotAuth.includes(path) || quiz) {
        return;
      }
      localStorage.clear();
      document.location.pathname = "/authorization";
    }
    if (
      originalRequest &&
      originalRequest.method?.toLowerCase() !== "get" &&
      BACKUP_BASEURLS.length > 0 &&
      isReserveStatus(status)
    ) {
      try {
        return await requestFromBackup(originalRequest);
      } catch (backupError) {
        return Promise.reject(backupError);
      }
    }

    throw error.response ?? error;
  }
);
